import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KYC_RESTRICTED_COUNTRIES } from '@incpt/shared';
import { BottomSheet } from './BottomSheet';
import { CloseIcon, FreezeIcon, SnowflakeIcon } from './icons';
import type { CardTemplate } from '../../lib/cards';
import { countryFlag } from '../../lib/countries';
import { cardRechargeFeePct, cardRechargeMinUsd, cardSpendLimit } from '@incpt/shared';

/** Issuer bank country when the live template list isn't loaded or the card is legacy. */
const TEMPLATE_ISSUER: Readonly<Record<number, { iso: string; country: string }>> = {
  6: { iso: 'HK', country: 'Hong Kong' },
  9: { iso: 'SG', country: 'Singapore' },
  14: { iso: 'US', country: 'USA' },
  24: { iso: 'HK', country: 'Hong Kong' },
  25: { iso: 'HK', country: 'Hong Kong' },
  26: { iso: 'HK', country: 'Hong Kong' },
  27: { iso: 'HK', country: 'Hong Kong' },
  28: { iso: 'US', country: 'USA' },
  32: { iso: 'SG', country: 'Singapore' },
};

/** Default when templateId is missing or unknown (current OneClick products are SG-issued). */
const DEFAULT_ISSUER = { iso: 'SG', country: 'Singapore' } as const;

function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M16.5 4.5l3 3L8 19l-3.5.5L5 16 16.5 4.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ConfirmButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex h-9 items-center justify-center rounded-full bg-[#377dff] px-4 text-white active:opacity-90"
      aria-label="Save"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M5 12.5l4.5 4.5L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-[14px] text-muted">{label}</span>
      <span className="text-[14px] font-medium text-ink">{value}</span>
    </div>
  );
}

function CheckRow({ label, supported = true }: { label: string; supported?: boolean }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className={`text-[16px] ${supported ? 'text-ink' : 'text-muted'}`}>{label}</span>
      {supported ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#34c759]">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12.5l4.5 4.5L19 7"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/10">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="text-muted"
            />
          </svg>
        </span>
      )}
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  cardNo?: number;
  frozen?: boolean;
  /** Ops hold — hide unfreeze. The API also rejects UNLOCK. */
  adminHold?: boolean;
  busy?: boolean;
  template?: CardTemplate | null;
  /** Fallback when the card's template isn't in the current offered list. */
  templateId?: number | null;
  cardLabel?: string | null;
  onRename?: (label: string) => void;
  onFreeze?: () => void;
  onUnfreeze?: () => void;
  onSetPin?: () => void;
  onDelete?: () => void;
};

export function CardSettingsSheet({
  open,
  onClose,
  cardNo = 1,
  frozen = false,
  adminHold = false,
  busy = false,
  template,
  templateId,
  cardLabel,
  onRename,
  onFreeze,
  onUnfreeze,
  onSetPin,
  onDelete,
}: Props) {
  const { t } = useTranslation();
  // Fixed product limits (same as issue flow). Fall back when the card's
  // template isn't in the current offered list (e.g. legacy BlueBanc).
  const limit = (n: number | undefined, key: 'perTx' | 'perDay' | 'perMonth') =>
    `${cardSpendLimit(n, key).toLocaleString('en-US')} USDT`;
  const defaultName = cardLabel || `${t('cardSettings.title')} #${cardNo}`;
  const [name, setName] = useState(defaultName);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [restrictedOpen, setRestrictedOpen] = useState(false);
  const tid = template?.templateId ?? templateId ?? null;
  const issuerFallback = tid != null ? TEMPLATE_ISSUER[tid] : undefined;
  const issuerIso = template?.countryIso || issuerFallback?.iso || DEFAULT_ISSUER.iso;
  const issuerCountry = template?.country || issuerFallback?.country || DEFAULT_ISSUER.country;
  const applePay = template?.applePay ?? (tid == null || ![6, 25, 26].includes(tid));
  const googlePay = template?.googlePay ?? true;
  const worksInUkraine = template?.worksInUkraine ?? tid === 25;

  // Reset name / confirm state whenever the sheet reopens.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setEditing(false);
      setConfirmDelete(false);
      setRestrictedOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const saveName = () => {
    setEditing(false);
    onRename?.(name.trim());
  };

  const restrictedList = t('cardSettings.restrictedCountriesList', {
    returnObjects: true,
    defaultValue: KYC_RESTRICTED_COUNTRIES,
  });
  const restrictedCountries = Array.isArray(restrictedList)
    ? (restrictedList as string[])
    : [...KYC_RESTRICTED_COUNTRIES];

  return (
    <>
      <BottomSheet open={open} onClose={onClose}>
        {/* Header — editable card name */}
        <div className="mt-4 flex items-center justify-between gap-3">
          {editing ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveName();
              }}
              className="min-w-0 flex-1 border-b border-ink/20 bg-transparent text-[18px] font-medium text-ink outline-none"
            />
          ) : (
            <button onClick={() => setEditing(true)} className="flex items-center gap-2 text-ink">
              <span className="text-[18px] font-medium">{name}</span>
              <span className="text-muted">
                <PencilIcon />
              </span>
            </button>
          )}
          {editing ? (
            <ConfirmButton onClick={saveName} />
          ) : (
            <button onClick={onClose} className="shrink-0 text-ink/70" aria-label="Close">
              <CloseIcon size={18} />
            </button>
          )}
        </div>

        {confirmDelete ? (
          <>
            <h2 className="mt-6 text-[18px] font-medium text-ink">{t('cardSettings.deleteTitle')}</h2>
            <p className="mt-3 text-[14px] leading-[1.5] text-muted">{t('cardSettings.deleteWarning')}</p>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={busy}
                className="h-14 flex-[2] rounded-full bg-[#f2f3f4] text-[15px] font-medium text-ink active:bg-black/[0.06] disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={onDelete}
                disabled={busy}
                className={`flex h-14 flex-[3] items-center justify-center gap-2 rounded-full bg-[#ff3b30] text-[15px] font-medium text-white ${
                  busy ? 'opacity-70' : 'active:opacity-90'
                }`}
              >
                {busy ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                ) : (
                  <TrashIcon />
                )}
                {t('cardSettings.deleteConfirm')}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Pay methods — same cues as the issue flow */}
            <div className="mt-5">
              <CheckRow label="Apple Pay" supported={applePay} />
              <div className="h-px bg-black/[0.06]" />
              <CheckRow label="Google Pay" supported={googlePay} />
              {worksInUkraine && (
                <>
                  <div className="h-px bg-black/[0.06]" />
                  <CheckRow label={t('cardSettings.worksInUkraine')} supported />
                </>
              )}
            </div>

            {/* Details — live from the card template */}
            <div className="mt-3 rounded-[20px] bg-[#f6f7f8] px-4 py-2.5">
              <DetailRow label={t('cardSettings.kyc')} value={t('cardSettings.kycValue')} />
              <DetailRow
                label={t('cardSettings.issuerJurisdiction')}
                value={`${countryFlag(issuerIso)} ${issuerCountry}`}
              />
              <DetailRow
                label={t('cardSettings.topUpFee')}
                value={`${cardRechargeFeePct(template?.rechargeFee, template?.templateId)}%`}
              />
              <DetailRow label={t('cardSettings.minTopUp')} value={`${cardRechargeMinUsd(template?.rechargeMin)} USDT`} />
              <DetailRow label={t('cardSettings.txLimit')} value={limit(template?.limitPerTx, 'perTx')} />
              <DetailRow label={t('cardSettings.dailyLimit')} value={limit(template?.limitPerDay, 'perDay')} />
              <DetailRow label={t('cardSettings.monthlyLimit')} value={limit(template?.limitPerMonth, 'perMonth')} />
            </div>

            <button
              type="button"
              onClick={() => setRestrictedOpen(true)}
              className="mt-3 w-full text-center text-[13px] text-muted underline underline-offset-2 active:opacity-70"
            >
              {t('cardSettings.restrictedCountriesLink')}
            </button>

            {/* Note */}
            <p className="mt-4 px-2 text-center text-[12px] leading-[1.5] text-muted">
              {t('cardSettings.note')}
            </p>

            {/* Set card PIN (only for PIN-capable cards) */}
            {onSetPin && (
              <button
                onClick={onSetPin}
                className="mt-3 flex h-12 w-full items-center justify-center rounded-full bg-[#f6f7f8] text-[15px] font-medium text-ink active:bg-black/[0.05]"
              >
                {t('cards.setPin')}
              </button>
            )}

            {/* Freeze / unfreeze. An ops hold cannot be cleared here. */}
            {!adminHold && (
            <button
              onClick={frozen ? onUnfreeze : onFreeze}
              disabled={busy}
              className={`mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#eef2ff] text-[15px] font-medium text-[#377dff] ${
                busy ? 'opacity-70' : 'active:bg-[#e0e8ff]'
              }`}
            >
              {busy ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#377dff]/30 border-t-[#377dff]" />
              ) : frozen ? (
                <SnowflakeIcon size={20} />
              ) : (
                <FreezeIcon size={20} />
              )}
              {frozen ? t('cardSettings.unfreezeCard') : t('cardSettings.freezeCard')}
            </button>
            )}

            {/* Delete — Banana CLOSE + local soft-hide */}
            {onDelete && !adminHold && (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                className={`mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#fff1f0] text-[15px] font-medium text-[#ff3b30] ${
                  busy ? 'opacity-70' : 'active:bg-[#ffe3e1]'
                }`}
              >
                <TrashIcon />
                {t('cardSettings.deleteCard')}
              </button>
            )}
          </>
        )}
      </BottomSheet>

      <BottomSheet
        open={restrictedOpen}
        onClose={() => setRestrictedOpen(false)}
        title={t('cardSettings.restrictedCountriesTitle')}
        onBack={() => setRestrictedOpen(false)}
      >
        <p className="mt-2 text-[13px] leading-[1.5] text-muted">
          {t('cardSettings.restrictedCountriesIntro')}
        </p>
        <ul className="mt-4 space-y-2.5 pb-2">
          {restrictedCountries.map((country) => (
            <li key={country} className="text-[15px] text-ink">
              {country}
            </li>
          ))}
        </ul>
      </BottomSheet>
    </>
  );
}
